import React, { useEffect, useRef, useState, CSSProperties } from "react";
import { useDispatch } from "react-redux";
import ImagerApi from "../../api/ImagerApi";
import IconDrag from "../../Icons/LiveImage/DragHandleIndicator";
import IconRuler from "../../Icons/LiveImage/Ruler";
import { EImagingModuleStatus } from "../../Model/ModuleState";
import { setLiveImageCropZoom } from "../../redux/actions/LiveImageAction";
import { getModuleState } from "../../redux/reducers/ModuleStateReducer";
import { useTypedSelector } from "../../redux/reducers/RootReducer";
import {
  getShowCrossHair,
  getShowRuler,
  getShowWellOverlay,
} from "./../../redux/reducers/LiveImageReducer";
import OverlayWellControl from "./OverlayWellControl";
import { ELightPath } from "../../Model/CaptureProfile";
import { getOneImagerCapabilities } from "../../redux/reducers/ImagerCapabilitiesReducer";
import {
  LightPathCapability,
  EFilterCube,
} from "../../Model/ImagerCapabilities";

function getVisibleFluorescence(
  lightPathCapability: LightPathCapability,
  filterCubeId: number
) {
  const filterCubes = lightPathCapability.optic.filterCubes;
  if (filterCubes === null || filterCubes === undefined) {
    return false;
  }
  if (filterCubeId === -1) filterCubeId = 0;
  const selectedFilterCube = filterCubes.filterCubes.filter(
    (fc) => fc.id === filterCubeId
  )[0];
  return selectedFilterCube.type === EFilterCube.VisibleFluorescence;
}

type Props = {
  imagerIdentifier: string;
  style?: CSSProperties;
};

function LiveImage({ imagerIdentifier, style = {} }: Props) {
  const dispatch = useDispatch();
  const [boundingRect, setBoundingRect] = useState({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  });

  const originalImageWidth = useTypedSelector(
    (state) =>
      state.moduleStateStore.moduleStateById[imagerIdentifier].imager
        ?.imageWidth ?? 100
  );

  const originalImageHeight = useTypedSelector(
    (state) =>
      state.moduleStateStore.moduleStateById[imagerIdentifier].imager
        ?.imageHeight ?? 100
  );

  const wellName = useTypedSelector(
    (state) =>
      state.moduleStateStore.moduleStateById[imagerIdentifier].imager
        ?.wellName ?? "A1"
  );

  const dropNumber = useTypedSelector(
    (state) =>
      state.moduleStateStore.moduleStateById[imagerIdentifier].imager
        ?.dropNumber ?? 1
  );

  const bestFit = useTypedSelector(
    (state) =>
      state.moduleStateStore.moduleStateById[imagerIdentifier].imager
        ?.bestFit ?? true
  );

  const imagerCapability = useTypedSelector((state) =>
    getOneImagerCapabilities(state, imagerIdentifier)
  );
  const imagerModuleState = useTypedSelector((state) =>
    getModuleState(state, imagerIdentifier)
  );
  const imagerState = imagerModuleState.imager;
  const isLiveStreamEnabled = imagerState?.freerunCapture;
  const manualControlModeEnabled =
    imagerState?.state === EImagingModuleStatus.Imageable;

  const activeLightPath = imagerState?.activeLightPath ?? ELightPath.Disabled;
  const lightPathCapability =
    imagerCapability.lightpathCollection.byId[activeLightPath];
  const activefilterCubeId =
    imagerState?.captureProfile.fluorescence?.filterCubeId;

  const isVisibleFluorescence = getVisibleFluorescence(
    lightPathCapability,
    activefilterCubeId ?? -1
  );

  const crosshairColor =
    activeLightPath === ELightPath.Fluorescence && isVisibleFluorescence
      ? "white"
      : "red";

  const fovX = useTypedSelector(
    (state) =>
      state.moduleStateStore.moduleStateById[imagerIdentifier].imager?.fovX ?? 1
  );
  // const fovY = useTypedSelector((state) => state.moduleStateStore.moduleStateById[imagerIdentifier].imager?.fovY ?? 1);
  const rulerLength = (fovX / originalImageWidth) * 82;

  const imageStream = useRef<HTMLImageElement>(null);
  const display = useRef<HTMLDivElement>(null);
  const overlay = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const showRuler = useTypedSelector((state) => getShowRuler(state));
  const showCroshair = useTypedSelector((state) => getShowCrossHair(state));
  const showWellOverlay = useTypedSelector((state) =>
    getShowWellOverlay(state)
  );

  useEffect(() => {
    if (
      imageStream?.current == null ||
      display?.current == null ||
      overlay?.current == null ||
      canvas?.current == null
    ) {
      return;
    }

    const displayWidth = display.current.clientWidth;
    const displayHeight = display.current.clientHeight;

    if (bestFit) {
      const ratio = Math.min(
        displayWidth / originalImageWidth,
        displayHeight / originalImageHeight
      );
      const scaledWidth = originalImageWidth * ratio;
      const scaledHeight = originalImageHeight * ratio;

      //------WIDTH & HEIGHT-------//
      imageStream.current.width = scaledWidth;
      imageStream.current.height = scaledHeight;
      canvas.current.width = scaledWidth;
      canvas.current.height = scaledHeight;
      overlay.current.style.width = scaledWidth + "px";
      overlay.current.style.height = scaledHeight + "px";

      //-------LEFT && RIGHT-------//
      const imageLeft = (displayWidth - scaledWidth) / 2;
      const imageTop = (displayHeight - scaledHeight) / 2;

      imageStream.current.style.left = imageLeft + "px";
      imageStream.current.style.top = imageTop + "px";
      canvas.current.style.left = imageLeft + "px";
      canvas.current.style.top = imageTop + "px";
      overlay.current.style.left = imageLeft + "px";
      overlay.current.style.top = imageTop + "px";
      const boundingRect = canvas.current.getBoundingClientRect();
      setBoundingRect(boundingRect);
      dispatch(setLiveImageCropZoom(1, 1));
    } else {
      //------WIDTH & HEIGHT-------//
      const canvasWidth = Math.min(displayWidth, originalImageWidth);
      const canvasHeight = Math.min(displayHeight, originalImageHeight);
      imageStream.current.width = originalImageWidth;
      imageStream.current.height = originalImageHeight;
      canvas.current.width = canvasWidth;
      canvas.current.height = canvasHeight;
      overlay.current.style.width = canvasWidth + "px";
      overlay.current.style.height = canvasHeight + "px";

      //-------LEFT && RIGHT-------//
      const imageLeft = (displayWidth - originalImageWidth) / 2;
      const imageTop = (displayHeight - originalImageHeight) / 2;
      const canvasLeft = imageLeft < 0 ? 0 : imageLeft;
      const canvasTop = imageTop < 0 ? 0 : imageTop;
      imageStream.current.style.left = imageLeft + "px";
      imageStream.current.style.top = imageTop + "px";
      canvas.current.style.left = canvasLeft + "px";
      canvas.current.style.top = canvasTop + "px";
      overlay.current.style.left = canvasLeft + "px";
      overlay.current.style.top = canvasTop + "px";

      const boundingRect = canvas.current.getBoundingClientRect();
      setBoundingRect(boundingRect);
      dispatch(
        setLiveImageCropZoom(
          originalImageWidth / canvasWidth,
          originalImageHeight / canvasHeight
        )
      );
    }
  }, [
    originalImageHeight,
    originalImageWidth,
    imageStream?.current?.naturalWidth,
    imageStream?.current?.naturalHeight,
    bestFit,
  ]);

  const onDoubleClick = (
    e: React.MouseEvent<HTMLCanvasElement, MouseEvent>
  ) => {
    if (
      imageStream == null ||
      imageStream.current == null ||
      display == null ||
      display.current == null ||
      overlay == null ||
      overlay.current == null ||
      canvas == null ||
      canvas.current == null ||
      !isLiveStreamEnabled ||
      !manualControlModeEnabled
    ) {
      return;
    }
    if (bestFit) {
      const mouseX = e.clientX - boundingRect.left;
      const mouseY = e.clientY - boundingRect.top;
      const width = boundingRect.right - boundingRect.left;
      const height = boundingRect.bottom - boundingRect.top;
      const centerX = width / 2;
      const centerY = height / 2;
      const distanceX = mouseX - centerX;
      const distanceY = centerY - mouseY;
      const percentageX = (distanceX / width) * 100;
      const percentageY = (distanceY / height) * 100;

      // console.log("LIVE_IMAGE", {
      //   rawX: e.clientX,
      //   rawY: e.clientY,
      //   width: width,
      //   height: height,
      //   mouseX: mouseX,
      //   mouseY: mouseY,
      //   centerX: centerX,
      //   centerY: centerY,
      //   distanceX: distanceX,
      //   distanceY: distanceY,
      //   percentageX: percentageX,
      //   percentageY: percentageY,
      // });
      // console.log("LIVE_IMAGE", { boundingRect });
      ImagerApi.armMoveRelativeByFov(
        imagerIdentifier,
        percentageX,
        percentageY
      );
    } else {
      const mouseX = e.clientX - boundingRect.left;
      const mouseY = e.clientY - boundingRect.top;
      const width = boundingRect.right - boundingRect.left;
      const height = boundingRect.bottom - boundingRect.top;
      const centerX = width / 2;
      const centerY = height / 2;
      const distanceX = mouseX - centerX;
      const distanceY = centerY - mouseY;
      const percentageX = (distanceX / originalImageWidth) * 100;
      const percentageY = (distanceY / originalImageHeight) * 100;

      // console.log("LIVE_IMAGE", {
      //   rawX: e.clientX,
      //   rawY: e.clientY,
      //   width: width,
      //   height: height,
      //   mouseX: mouseX,
      //   mouseY: mouseY,
      //   centerX: centerX,
      //   centerY: centerY,
      //   distanceX: distanceX,
      //   distanceY: distanceY,
      //   percentageX: percentageX,
      //   percentageY: percentageY,
      //   imageWidth: imageWidth,
      //   imageHeight: imageHeight,
      // });
      // console.log("LIVE_IMAGE", { boundingRect });
      ImagerApi.armMoveRelativeByFov(
        imagerIdentifier,
        percentageX,
        percentageY
      );
    }
  };

  const src =
    "//" + window.location.hostname + ":" + imagerState?.streamServerPort;

  const plate = imagerState?.plate;

  let selectedCol: number = 0;
  let selectedRow: number = 1;

  if (plate != null && imagerState != null) {
    selectedRow = Math.floor(
      (imagerState.wellNumber - 1) / plate.plateType.numberOfColumn
    );
    selectedCol =
      imagerState.wellNumber - 1 - selectedRow * plate.plateType.numberOfColumn;
  }

  // useEffect(() => {
  //   const currentFov = getCurrentFov(originalImageWidth, fovX, fovY, 82);
  //   // dispatch(setFov(currentFov));
  // }, [originalImageWidth, fovX, fovY, dispatch]);

  console.log("LIVE IMAGE RENDERED");

  return (
    <div
      ref={display}
      style={{
        height: "100%",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        ...style,
      }}
    >
      {/* -------Well Overlay------- */}
      {showWellOverlay && plate && (
        <OverlayWellControl
          style={{
            zIndex: 20,
          }}
          numberOfCol={plate.plateType.numberOfColumn}
          numberOfRow={plate.plateType.numberOfRow}
          selectedCol={selectedCol}
          selectedRow={selectedRow}
          drop={dropNumber}
          imagerIdentifier={imagerIdentifier}
        />
      )}
      <div
        ref={overlay}
        style={{
          position: "absolute",
          zIndex: 1,
        }}
      >
        {/* -------Crosshair------- */}
        {showCroshair && (
          <svg
            preserveAspectRatio="none"
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
              height: "100%",
            }}
            viewBox="0 0 1001 1001"
          >
            <line
              x1="0"
              y1="500"
              x2="1000"
              y2="500"
              stroke={crosshairColor}
              strokeWidth="2"
            />
            <line
              x1="500"
              y1="0"
              x2="500"
              y2="1000"
              stroke={crosshairColor}
              strokeWidth="2"
            />
          </svg>
        )}
        {/* -------DropNumber------- */}
        <div
          style={{
            position: "absolute",
            left: 12,
            // height: 70,
            fontSize: "44px",
            fontWeight: 700,
            color: "#A6A6A7",
          }}
        >
          {wellName + "-" + dropNumber}
        </div>
        {/* -------Ruler------- */}
        {showRuler && (
          <div
            style={{
              backgroundColor: "gray",
              position: "absolute",
              left: 12,
              bottom: 37,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              paddingRight: 11,
            }}
          >
            <IconDrag />
            <IconRuler
              style={{
                marginLeft: 12,
                marginRight: 9,
              }}
            />
            <div>{rulerLength.toFixed(0) + "µM"}</div>
          </div>
        )}
      </div>

      <img
        style={{
          position: "absolute",
          // transform: "translateX(-50%)",
          // left: "50%",
        }}
        ref={imageStream}
        src={src}
        alt="..."
      />
      <canvas
        id="CANVAS"
        ref={canvas}
        onDoubleClick={onDoubleClick}
        style={{
          position: "absolute",
          // top: "0px",
          // left: "50%",
          // bottom: "0px",
          // right: "0px",
          zIndex: 1,
          userSelect: "none",
          // transform: "translateX(-50%)",
        }}
      />
    </div>
  );
}

function areEqual(prevProps: Props, nextProps: Props) {
  return prevProps.imagerIdentifier === nextProps.imagerIdentifier;
}

export default React.memo(LiveImage, areEqual);

//LiveImage.whyDidYouRender = true;
